import { initializeApp } from 'firebase/app';
import {
  initializeAppCheck,
  ReCaptchaV3Provider,
  getToken,
  type AppCheck,
} from 'firebase/app-check';
import { getAuth, GoogleAuthProvider } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

export const firebaseApp = initializeApp(firebaseConfig);

// Firebase App Check (free abuse/bot protection — replaces the torn-down WAF).
// Initialized ONLY when a reCAPTCHA v3 site key is provided, so local/dev runs
// without a key continue to work unchanged. The site key is PUBLIC and safe to
// ship in the frontend bundle (the reCAPTCHA SECRET lives only in the Firebase
// Console). When configured, the Firebase SDK automatically attaches the App
// Check token to Firestore/Functions calls; for our bespoke backend fetch()
// calls we attach it explicitly via appCheckHeaders() below.
const APPCHECK_RECAPTCHA_KEY = process.env.REACT_APP_APPCHECK_RECAPTCHA_KEY;
let appCheck: AppCheck | undefined;
if (APPCHECK_RECAPTCHA_KEY) {
  appCheck = initializeAppCheck(firebaseApp, {
    provider: new ReCaptchaV3Provider(APPCHECK_RECAPTCHA_KEY),
    isTokenAutoRefreshEnabled: true,
  });
}

/**
 * Resolve the current App Check token as a header object suitable for spreading
 * into a fetch() Headers init. Returns {} when App Check is not configured (no
 * site key) or when token minting fails — backend defaults to monitor mode, so
 * a missing header must never break a legitimate request.
 */
export async function appCheckHeaders(): Promise<Record<string, string>> {
  if (!appCheck) return {};
  try {
    const { token } = await getToken(appCheck, /* forceRefresh */ false);
    return token ? { 'X-Firebase-AppCheck': token } : {};
  } catch {
    return {};
  }
}

export const auth = getAuth(firebaseApp);
export const db = getFirestore(firebaseApp);
export const googleProvider = new GoogleAuthProvider();
